import { Injectable } from '@angular/core';
import { Observable, Observer } from 'rxjs/Rx';
import 'rxjs/add/operator/map';
import { ReplaySubject, Subject } from 'rxjs/';

import { CommunicationService } from './communication.service';
import { User } from '../models/user';
import { Study } from '../models/study';
import { HCPPendingRequest } from '../models/hcppending-request';
import { BasicError, Error403 } from '../models/error/error';

@Injectable()
export class HCPGroupService {
  private collaboratingStudiesUrl = '/api/v1/editor/studies';
  private collaboratingReplay: Subject<any> = null; // ReplaySubject of get CollaboratingStudies
  private collaboratingStudiesThreshold = 0; // Until this time the cached collaborating studies will be returned
  private collaboratingStudiesThresholdAddTime = 4 * 60 * 1000; // 4 minutes threshold should be enough
  private collaboratingStudiesLoaded = false; // Set is true if collaborating studies was loaded once
  private collaboratingStudiesOnload = false; // If a request is currently loading
  private collaboratingStudiesUnauthorized = false; // Set is true if unauthorized was returned (403)
  private collaboratingStudies: Study[] = [];
  private amountCollaboratingStudies: Number = 0;

  /*
    Vars for getMembers
  */
  private membersOfStudieUrl = '/api/v1/studies/##id##/members';
  private membersOfStudieReplay = {};
  private membersOfStudieCurrPage = {};

  /*
    Vars for getCollaborators
  */
  private collaboratorsOfStudyUrl = '/api/v1/studies/##id##/collaborators';
  private collaboratorsOfStudyReplay = {};
  private collaboratorsOfStudyCurrPage = {};

  /*
    Vars for removing Members of study
  */
  private removeMemberURL = '/api/v1/studies/##sid##/relationships/members';
  private addMemberURL = '/api/v1/studies/##sid##/relationships/members';
  private pendingRequestURL = '/api/v1/editor/studies/##sid##/requests';
  private acceptPendingRequestURL = '/api/v1/editor/studies/##sid##/requests/';
  private deletePendingRequestURL = '/api/v1/editor/studies/##sid##/requests/';
  private updateStudyURL = '/api/v1/studies/';
  private addColaboratorURL = '/api/v1/studies/##sid##/relationships/collaborators';

  constructor(private communication: CommunicationService) { }

  /**
   * This function returns all collaboratingStudies of the current user.
   * @return ReplaySubject<any> - can contain error
   */
  public getCollaboratingStudies(currentPage: number = 1): Subject<any> {
    if (this.collaboratingReplay === null) {
      this.collaboratingReplay = new Subject();
    }

    if (this.collaboratingStudiesLoaded && !this.collaboratingStudiesUnauthorized &&
      this.collaboratingStudiesThreshold > (new Date().getTime())) {
      // Returning cached data of collaborating studies
      const tmpReplay = new ReplaySubject(1);
      tmpReplay.next(this.collaboratingStudies);
      this.collaboratingStudiesLoaded = false;
      return tmpReplay;
    }

    if (this.collaboratingStudiesOnload) {
      return this.collaboratingReplay;
    } else {
      this.collaboratingStudiesOnload = true;
      this.collaboratingStudies = [];
    }

    // No threshold data found, the data will be fetched from api
    const request = this.communication.get(this.collaboratingStudiesUrl + '?page=' + currentPage + '&limit=0');
    request.subscribe(json => {
      // Check if an error returned
      if (json instanceof BasicError) {
        // Error was returned, check if it was a Error403
        if (json instanceof Error403) {
          // User isn't authorized to fetch data, consider user as non-editor
          console.log('---HCPGroupService---: User is not authorized to get collaborating studies.');
          this.collaboratingStudiesUnauthorized = true;
        }

        this.collaboratingStudiesThreshold = 0;
        this.collaboratingReplay.next(json);
        this.collaboratingStudiesOnload = false;
        return;
      }

      // No Error occured, user is role of editor, try to map data into list objects
      this.amountCollaboratingStudies = json.meta.pagination.total;
      const total: number = json.meta.pagination.total_pages;
      const retArr: Study[] = [];

      for (let i = 0; i < json.data.length; i++) {
        const stdJSON = json.data[i];
        const study = new Study(stdJSON.id);
        if (stdJSON.attributes.name) { study.setName(stdJSON.attributes.name) };
        if (stdJSON.attributes.title) { study.setTitle(stdJSON.attributes.title) };
        if (stdJSON.attributes.description) { study.setDescription(stdJSON.attributes.description) };
        if (stdJSON.attributes.accesstype) { study.setAccesstype(stdJSON.attributes.accesstype) };
        if (stdJSON.attributes.starts_at) { study.setStarts_at(stdJSON.attributes.starts_at) };
        if (stdJSON.attributes.ends_at) { study.setEnds_at(stdJSON.attributes.ends_at) };
        if (stdJSON.attributes.is_private) { study.setIs_private(stdJSON.attributes.is_private) };
        (stdJSON.attributes.is_running) ? study.setIs_running(true) : study.setIs_running(false);
        retArr.push(study);
      }

      // Setting all vars
      this.collaboratingStudies = this.collaboratingStudies.concat(retArr);
      if (currentPage >= total) {
        this.collaboratingStudiesThreshold = new Date().getTime() + this.collaboratingStudiesThresholdAddTime;
        this.collaboratingStudiesLoaded = true;
      } else {
        this.getCollaboratingStudies((currentPage + 1));
      }
      this.collaboratingStudiesUnauthorized = false;
      this.collaboratingStudiesOnload = false;
      this.collaboratingReplay.next(retArr);
    });

    return this.collaboratingReplay;
  }

  /**
   * This function returns all members of an study
   * @param studyId id of group
   */
  public getMembersOfStudy(studyId: String) {
    if (this.membersOfStudieReplay[studyId.toString()] === undefined || this.membersOfStudieReplay[studyId.toString()] === null) {
      this.membersOfStudieReplay[studyId.toString()] = new Subject();
      this.membersOfStudieCurrPage[studyId.toString()] = 1;
    } else if (this.membersOfStudieCurrPage[studyId.toString()] === 1) {
      return this.membersOfStudieReplay[studyId.toString()];
    }

    // Getting data from api
    const request = this.communication.get((this.membersOfStudieUrl.replace('##id##',
      studyId.toString())) + '?page=' + this.membersOfStudieCurrPage[studyId.toString()] + '&limit=0');
    request.subscribe(json => {
      // Check if an error returned
      if (json instanceof BasicError) {
        // Error was returned, check if it was a Error403
        if (json instanceof Error403) {
          // User isn't authorized to fetch data, consider user as non-editor
          console.log('---HCPGroupService---: User is not authorized to get amount members [getAmountMembers].');
        }
        console.log('---HCPGroupService---: Error occured, threshold will be set to 0 [getAmountMembers].')
        this.membersOfStudieReplay[studyId.toString()].next(json);
        this.membersOfStudieReplay[studyId.toString()] = null;
        return;
      }

      // No Error occured, user is role of editor, try to map data into list objects

      // Initialize the members array
      const total: number = json.meta.pagination.total_pages;
      const ret = [];

      // Getting all members
      for (let i = 0; i < json.data.length; i++) {
        const js = json.data[i];
        const user = new User(js.id);

        if (js.attributes.name) { user.setName(js.attributes.name) };
        if (js.attributes.email) { user.setEmail(js.attributes.email) };
        if (js.attributes.firstname) { user.setFirstName(js.attributes.firstname) };
        if (js.attributes.lastname) { user.setLastName(js.attributes.lastname) };
        if (js.attributes.picture) { user.setPicture(js.attributes.picture) };
        if (js.attributes.role) { user.setRole(js.attributes.role) };
        if (js.attributes.sex) {
          switch (js.attributes.sex) {
            case 1: user.setSex('männlich');
              break;
            case 2: user.setSex('weiblich');
              break;
            default: user.setSex('neutral');
          }
        }

        ret.push(user);
      }


      if (this.membersOfStudieCurrPage[studyId.toString()] >= total) {
        this.membersOfStudieReplay[studyId.toString()].next(ret);
        this.membersOfStudieReplay[studyId.toString()] = null;
      } else {
        this.membersOfStudieReplay[studyId.toString()].next(ret);
        this.membersOfStudieCurrPage[studyId.toString()] = this.membersOfStudieCurrPage[studyId.toString()] + 1;
        this.getMembersOfStudy(studyId);
      }
    });

    return this.membersOfStudieReplay[studyId.toString()];
  }

  /**
   * This function returns all collaborators
   * @param studyId group id of study
   */
  public getCollaboratorsOfStudy(studyId: String) {
    if (this.collaboratorsOfStudyReplay[studyId.toString()] === undefined || this.collaboratorsOfStudyReplay[studyId.toString()] === null) {
      this.collaboratorsOfStudyReplay[studyId.toString()] = new Subject();
      this.collaboratorsOfStudyCurrPage[studyId.toString()] = 1;
    }

    // Getting data from api
    const request = this.communication.get((this.collaboratorsOfStudyUrl.replace('##id##',
      studyId.toString())) + '?page=' + this.collaboratorsOfStudyCurrPage[studyId.toString()] + '&limit=0');
    request.subscribe(json => {
      // Check if an error returned
      if (json instanceof BasicError) {
        // Error was returned, check if it was a Error403
        if (json instanceof Error403) {
          // User isn't authorized to fetch data, consider user as non-editor
          console.log('---HCPGroupService---: User is not authorized to get amount members [getAmountMembers].');
        }
        console.log('---HCPGroupService---: Error occured, threshold will be set to 0 [getAmountMembers].')
        this.collaboratorsOfStudyReplay[studyId.toString()].next(json);
        this.collaboratorsOfStudyReplay[studyId.toString()] = null;
        return;
      }

      // No Error occured, user is role of editor, try to map data into list objects

      // Initialize the members array
      const total: number = json.meta.pagination.total_pages;
      const ret = [];

      // Getting all members
      for (let i = 0; i < json.data.length; i++) {
        const js = json.data[i];
        const user = new User(js.id);

        if (js.attributes.name) { user.setName(js.attributes.name) };
        if (js.attributes.email) { user.setEmail(js.attributes.email) };
        if (js.attributes.firstname) { user.setFirstName(js.attributes.firstname) };
        if (js.attributes.lastname) { user.setLastName(js.attributes.lastname) };
        if (js.attributes.picture) { user.setPicture(js.attributes.picture) };
        if (js.attributes.role) { user.setRole(js.attributes.role) };
        if (js.attributes.sex) {
          switch (js.attributes.sex) {
            case 1: user.setSex('männlich');
              break;
            case 2: user.setSex('weiblich');
              break;
            default: user.setSex('neutral');
          }
        }

        ret.push(user);
      }

      if (this.collaboratorsOfStudyCurrPage[studyId.toString()] >= total) {
        this.collaboratorsOfStudyReplay[studyId.toString()].next(ret);
        this.collaboratorsOfStudyReplay[studyId.toString()] = null;
      } else {
        this.collaboratorsOfStudyReplay[studyId.toString()].next(ret);
        this.collaboratorsOfStudyCurrPage[studyId.toString()]++;
        this.getCollaboratorsOfStudy(studyId);
      }
    });

    return this.collaboratorsOfStudyReplay[studyId.toString()];
  }

  /**
   * Removing users from study
   * @param study Study to be removed from
   * @param usersId userIds to remove
   */
  public removeMembersFromStudy(study: Study, usersId: Array<String>) {
    const retReplay = new Subject();

    const idData = [];

    for (const id of usersId) {
      idData.push({ 'id': id });
    }

    const postData = {
      'data': {
        'type': 'users',
        'attributes': {
          'users': idData
        }
      }
    };

    this.communication.delete(this.removeMemberURL.replace('##sid##', study.getId().toString()), postData).subscribe(json => {
      if (json instanceof BasicError) {
        console.log('------HCPGroupService------: Failed to delete users');
        retReplay.next(false);
      } else {
        // Successfully deleted users
        retReplay.next(true);
      }
    });

    return retReplay;
  }

  /**
   * Function to add a user to a specific study
   * @param study study of user
   * @param userIds userids to be added to study
   */
  public addMemberToStudy(study: Study, userIds: Array<String>) {
    const retReplay = new Subject();

    const idData = [];

    for (const id of userIds) {
      idData.push({ 'id': id });
    }

    const postData = {
      'data': {
        'type': 'users',
        'attributes': {
          'users': idData
        }
      }
    };

    this.communication.post(this.addMemberURL.replace('##sid##', study.getId().toString()), postData).subscribe(json => {
      if (json instanceof BasicError) {
        console.log('------HCPGroupService------: Failed to delete users');
        retReplay.next(false);
      } else {
        // Successfully deleted users
        retReplay.next(true);
      }
    });

    return retReplay;
  }

  /**
   * This function returns all pending requests
   * @param study study the request will be retrieved from
   */
  public getPendingRequests(study: Study): Subject<any> {
    const retReplay = new Subject();

    // Getting all pending requests
    this.communication.get(this.pendingRequestURL.replace('##sid##', study.getId().toString())).subscribe(json => {
      if (json instanceof BasicError) {
        console.log('------HCPGroupService-----: Some error occured while retrieving pending requests.');
        retReplay.next(json);
        return;
      }

      // Getting pending request
      const pendings = [];

      for (const pending of json.data) {
        // Getting Id and user informations
        const id = pending.id;
        const user = new User(pending.id);

        if (pending.attributes.name !== undefined) {
          user.setName(pending.attributes.name);
        }

        if (pending.attributes.email !== undefined) {
          user.setEmail(pending.attributes.email);
        }

        if (pending.attributes.firstname !== undefined) {
          user.setFirstName(pending.attributes.firstname);
        }

        if (pending.attributes.lastname !== undefined) {
          user.setLastName(pending.attributes.lastname);
        }

        if (pending.attributes.picture !== undefined) {
          user.setPicture(pending.attributes.picture);
        }

        const pendingRequest = new HCPPendingRequest(id);
        pendingRequest.setUser(user);

        pendings.push(pendingRequest);
      }

      retReplay.next(pendings);
    });

    return retReplay;
  }

  /**
   * Function to accept a pending request
   * @param study study
   * @param pending pending request to be accepted
   */
  public acceptPendingRequest(study: Study, pending: HCPPendingRequest): Subject<any> {
    const retReplay = new Subject();

    const postData = {
      'data': {
        'type': 'users',
        'attributes': {
          'code': 'accepted'
        }
      }
    };

    const url = this.acceptPendingRequestURL.replace('##sid##', study.getId().toString()) + pending.getId();

    this.communication.post(url, postData).subscribe(json => {
      if (json instanceof BasicError) {
        console.log('-----HCPGroupService----: Failed to accept invitation');
        console.log(json);
        retReplay.next(false);
      } else {
        retReplay.next(true);
      }
    });

    return retReplay;
  }


  /**
   * Deleting a pending request
   * @param study study from invitation
   * @param pending request to be deleted
   */
  public deletePendingRequest(study: Study, pending: HCPPendingRequest): Subject<any> {
    const retReplay = new Subject();


    const url = this.deletePendingRequestURL.replace('##sid##', study.getId().toString()) + pending.getId();

    this.communication.delete(url).subscribe(json => {
      if (json instanceof BasicError) {
        console.log('-----HCPGroupService----: Failed to delete invitation');
        console.log(json);
        retReplay.next(false);
      } else {
        retReplay.next(true);
      }
    });

    return retReplay;
  }

  /**
   * Updating mode of study
   * @param study study to change mod to
   * @param is_private setting private to true or false
   * @param accesstype setting accesstype (invite or password)
   * @param password [optional] password if accesstype is password
   */
  public setStudyMode(study: Study, is_private: Boolean, accesstype: String, password: String = null): Subject<any> {
    const retReplay = new Subject();

    const postData = {
      'data': {
        'type': 'studies',
        'attributes': {
          'is_private': is_private
        }
      }
    };

    if (accesstype !== null) {
      postData.data.attributes['accesstype'] = accesstype;
    }

    if (accesstype !== null && accesstype === 'password') {
      postData.data.attributes['password'] = password;
    }

    this.communication.patch(this.updateStudyURL + study.getId().toString(), postData).subscribe(json => {
      if (json instanceof BasicError) {
        console.log('-----HCPGroupService-----: Could not update study mode');
        retReplay.next(false);
        return;
      }

      retReplay.next(true);
    });

    return retReplay;
  }

  /**
   * This function adds a user as a collaborator
   * @param study study the user should get collaborator of
   * @param user the user to be collaborator
   * @param owner if the user should be study owner
   */
  public addCollaboratorToStudy(study: Study, user: User, owner: Boolean = false): Subject<any> {
    const retReplay = new Subject();

    const postUser = { 'id': user.getID().toString() };
    if (owner) {
      postUser['role'] = 'study.owner';
    } else {
      postUser['role'] = 'study.access';
    }

    const postData = {
      'data': {
        'type': 'users',
        'attributes': {
          'users': [postUser]
        }
      }
    }

    this.communication.post(this.addColaboratorURL.replace('##sid##', study.getId().toString()), postData).subscribe(json => {
      if (json instanceof BasicError) {
        console.log('-----HCPGroupService---: Could not add collaborator');
        console.log(json);
        retReplay.next(false);
      } else {
        retReplay.next(true);
      }
    });

    return retReplay;
  }

  /**
   * Removing a user from collaborator list
   * @param study study the user should be removed from
   * @param user the user that should be removed
   */
  public removeCollaboratorFromStudy(study: Study, user: User): Subject<any> {
    const retReplay = new Subject();

    const postUser = { 'id': user.getID().toString() };

    const postData = {
      'data': {
        'type': 'users',
        'attributes': {
          'users': [postUser]
        }
      }
    }

    this.communication.delete(this.addColaboratorURL.replace('##sid##', study.getId().toString()), postData).subscribe(json => {
      if (json instanceof BasicError) {
        console.log('-----HCPGroupService---: Could not remove collaborator');
        console.log(json);
        retReplay.next(false);
      } else {
        retReplay.next(true);
      }
    });

    return retReplay;
  }

}



// WEBPACK FOOTER //
// C:/Users/andyw/Documents/Uni/Master/Masterprojekt/Angular/src/app/services/hcpgroup.service.ts